import { LightningElement, track, api } from 'lwc';

/*
    --Example setup WITH DEPENDENT FIELDS:
        modalDependentFields = {
            controlField: {
                fieldName: 'instrument_type', label: 'Instrument Type', placeHolder: 'ex: Shares...', isRequired: true, dtype: 'picklist', 
                    options: [
                        {label: 'label1', value: 'value1'},
                        {label: 'label2', value: 'value2'},
                    ],
            },
            dependentFields: [
                { 
                    validValues: ['value1'],  // if <controlField> = value1, show the field in the modal
                    field: { 
                        fieldName: 'field1', 
                        label: 'field1', 
                        isRequired: true, 
                        dtype: 'date', 
                        placeHolder: ''
                    } 
                },
                { 
                    validValues: ['value1', 'value2'], // if <controlField> = value1 or value2, show the fields in the modal
                    field: { 
                        fieldName: 'field2', 
                        label: 'field2', 
                        isRequired: true, 
                        dtype: 'currency', 
                        placeHolder: ''
                    } 
                },
            ]
        };
        myFields = [
                {fieldName: 'field1', dtype: 'text'},
                {fieldName: 'field2', dtype: 'number', isRequired: true},
            ];
            this.querySelector('x-slds-modal').showMe()

        Add the component to the markup (HTML):
            <x-slds-modal-form
                fields={myFields}
                onmodalclick={handleModalClick}
                dependent-fields={modalDependentFields}
            ></x-slds-modal-form>

        Handle the modal message (JS):
            handleModalClick(message) {
                const detail = message.detail; 
                if (detail === 'cancel') { // do something }
                if (detail === 'okay') { // do something else }
            }

    --Example setup WITHOUT DEPENDENT FIELDS:
        Initialize the modal (JS):
            myFields = [
                {fieldName: 'field1', dtype: 'text'},
                {fieldName: 'field2', dtype: 'number', isRequired: true},
                {fieldName: 'field3', dtype: 'percent'},
                {fieldName: 'field4', dtype: 'picklist', isRequired: true}
            ];
            this.querySelector('x-slds-modal').showMe()

        Add the component to the markup (HTML):
            <x-slds-modal-form
                fields={myFields}
                onmodalclick={handleModalClick}
            ></x-slds-modal-form>

        Handle the modal message (JS):
            handleModalClick(message) {
                const detail = message.detail; 
                if (detail === 'cancel') { // do something }
                if (detail === 'okay') { // do something else }
            }
*/

export default class sldsModalForm extends LightningElement {
    static renderMode = 'light';

    @track _dependentFields;
    @track dependentSelection = null;

    @track displayModal = false;
    @track fieldForm = {};    // Structure the data as a record object
    @track _requiredFields = []; // Store the required fields
    @track _fields;


    // ======================== Public Properties ============================ \\ 
    @api columns;          // adjust grid cols to 'slds-size_1-of-<columns>'
    @api size;            // apply slds-modal_<size> to the modal component 
    @api title = 'New';  // Modal Header
    @api // If dependent fields --> display a modal before showing the entire fields form
    set dependentFields(value) {
        this._dependentFields = value;
    }
    get dependentFields() {
        return this._dependentFields;
    }
    @api // Parse the fields sent by the parent && add custom object properties
    set fields(value) {  
        // Reset the variables everytime the Modal opens
        this._requiredFields = [];
        this._fields = []; 
        if (value) {
            this.updateRequiredFieldsArr(value);
            this.updateFormFields(value);
        } 
    }
    get fields() {
        return this._fields;
    }
    // ======================================================================= \\


    // Adjust slds-size_1-of-2 to <this.columns> digit
    renderedCallback() {
        // Adjust Columns per row if specified (slds-size_1-of<columns>)
        if (this.columns && this.columns !== 2) {
            const columns = parseInt(this.columns);                  // Convert the column amount to an int
            const gridCols = this.querySelectorAll('.field-row');   // Get all rows in the slds-grid
            if (gridCols) {
                const newClass = `slds-size_1-of-${columns}`;     // Define the new class (slds-size_1-of<columns>)
                gridCols.forEach(grid => 
                    grid.classList.replace('slds-size_1-of-2', newClass)
                );                                             // Replace slds-size_1-of-1
            }
        }
    }
    // Apply size styles to the modal (on "slds-modal slds-fade-in-open")
    get modalCl() {
        const sizeLower = this.size ? this.size.toLowerCase() : null;
        if (sizeLower && ['small', 'medium', 'large'].some(sz => sz === sizeLower)) {
            // Make sure the size parameter is an available size
            return `slds-modal slds-fade-in-open slds-modal_${sizeLower}`;
        }
        return 'slds-modal slds-fade-in-open';
    }


    // ================ Update <fields> and <requiredFields> ================ \\ 
    // Reactively update <this._requiredFields>
    updateRequiredFieldsArr(fieldsParam) {
        // Do nothing if argument is blank
        if (!fieldsParam) return;

        // Get the Required Fields in <fieldsParam>
        const newRequiredFields = JSON.parse(JSON.stringify(fieldsParam)).filter(field => field.isRequired)
            .map(field => field.fieldName);

        // Add the new fields to <this._fields>
        if (this._requiredFields && this._requiredFields.length > 0) {
            this._requiredFields = this._requiredFields.concat(newRequiredFields);
            this._requiredFields = [...this._requiredFields];
            return;
        }

        // Create a new array for reactivity
        this._requiredFields = [...newRequiredFields];
    }
    // Reactively update <this._fields>
    updateFormFields(fieldsParam) {
        if (!fieldsParam || fieldsParam.length < 1) {
            // Do nothing if fieldsParam is blank
            return;
        }

        // Get the fields in the <fieldsParam>
        const newFields = fieldsParam.map(field => {
            return { 
                ...field, 
                [`is${field.dtype || 'text'}`]: true,
            };
        });

        // Add the new fields to <this._fields>
        if (this._fields && this._fields.length > 0) {
            this._fields = this._fields.concat(newFields);
            this._fields = this._fields;
            return;
        }

        // Create a new array for reactivity
        this._fields = [...newFields];

        const valFields = this._fields.filter(f => f.value);
        if (valFields && valFields.length) {
            valFields.forEach(valF => {
                this.fieldForm[valF.fieldName] = valF.value;
            });
        }        
    }

    // Update formula fields 
    updateFormulaFields() {
        // Update disabled fields by formula if formula present
        if (
            this._fields && 
            this._fields.length && 
            this._fields.some(f => f.formula) 
        ) {
            console.log('IN updateFormulaFields() AND THERE IS FORMULA');
           
            this._fields = this._fields.map(f => {
                if (f.hasOwnProperty('formula')) {
                    const res = f.formula(this.fieldForm);
                    if (res || res === 0) {
                        this.fieldForm[f.fieldName] = res;
                    }
                    return {...f, value: res};
                }
                return f;
            })
        }
    }

    // Check for min/max errors
    isMinMaxBreached(fieldName, newVal) {
        if (!this._fields.some(f => f.min || f.max)) return null;

        // Find the field
        const matched = this._fields.find(f => f.fieldName === fieldName);
        if (!matched || (!matched.min && !matched.max)) return null;

        // Store the min and the max breaches
        const minMaxObj = {};

        // Check if field val is number
        const isNumeric = ['number', 'decimal', 'percent', 'currency']
            .some(d => matched.dtype === d);
        // Check if field val is Date/Datetime
        const isDateType = matched.dtype === 'date' || matched.dtype === 'datetime';

        let newValNorm = newVal;      // Store newVal
        let minNorm = typeof matched.min === 'function' ? matched.min(this.fieldForm) : matched.min;   // Store min 
        let maxNorm = typeof matched.max === 'function' ? matched.max(this.fieldForm) : matched.max;  // Store max

        console.log('herre is minNorm: ', minNorm);
        console.log('herre is maxNorm: ', maxNorm);
        console.log('And  here  is newVal: ', newVal);
        if (isNumeric) {
            // Normalize the min/max/newVal to numeric
            newValNorm = Number(newVal);
            minNorm = matched.min != null ? Number(matched.min) : null;
            maxNorm = matched.max != null ? Number(matched.max) : null;
        } else if (isDateType) {
            // normalize the min/max/newVal to Dates
            newValNorm = new Date(newVal).getTime();
            minNorm = matched.min ? new Date(matched.min).getTime() : null;
            maxNorm = matched.max ? new Date(matched.max).getTime() : null;
        }


        // Check if Min breached
        if (minNorm !== null && newValNorm < minNorm) {
            console.log('newVal < matched.min:', newValNorm, minNorm);
            minMaxObj.min = matched.min;
        }
        // Check if Max breached
        if (maxNorm !== null && newValNorm > maxNorm) {
            console.log('newVal > matched.max:', newValNorm, maxNorm);
            minMaxObj.max = matched.max;
        }



        if (minMaxObj.min || minMaxObj.max) {
            // Create an Error Message
            const message =
                minMaxObj.min && minMaxObj.max
                    ? `${matched.label} must be between ${minMaxObj.min} and ${minMaxObj.max}`
                    : minMaxObj.max
                    ? `${matched.label} must be less than ${minMaxObj.max}`
                    : `${matched.label} must be greater than ${minMaxObj.min}`;
            return message;
        }

        return null;
    }


    /* Check for min/max errors
    isMinMaxBreached(fieldName, newVal) {
        if (this._fields.some(f => f.min || f.max)) {
            // Find the field 
            const matched = this._fields.find(f => f.fieldName === fieldName);

            // Check if <newVal> is numeric
            const isNumeric = ['number', 'decimal', 'percent', 'currency'].some(d => matched.dtype === d);

            // Convert to number if dtype numeric
            const cleanNewVal = isNumeric ? Number(newVal) : newVal;

            if (matched && (matched.min || matched.max)) {
                const minMaxObj = {};
                if (matched.min && (cleanNewVal < matched.min)) {
                    console.log('cleanNewVal < matched.min:  ', ' cleanNewVal: ', cleanNewVal, ' matched.min: ', matched.min);
                    minMaxObj.min = matched.min;
                }
                if (matched.max && (cleanNewVal > matched.max)) {
                    console.log('cleanNewVal > matched.max:  ', ' cleanNewVal: ', cleanNewVal, ' matched.max: ', matched.max);
                    minMaxObj.max = matched.max;
                }
                console.log('HERE IS MIN MAX: ', JSON.stringify(minMaxObj), ' And here is the  new val: ', cleanNewVal);
                if (minMaxObj && (minMaxObj.min || minMaxObj.max)) {
                    const message = minMaxObj.min && minMaxObj.max
                        ?  `${matched.label} must be between ${minMaxObj.min} and ${minMaxObj.max}`
                            : minMaxObj.max 
                        ? `${matched.label} must be less than ${minMaxObj.max}`
                        : `${matched.label} must be greater than ${minMaxObj.min}`;
                    return message;
                }
            }
        }
        return null;
    }
    */
    // ======================================================================= \\

    

    // ======================= Handle Dependent Fields ======================= \\ 
    // Add dependent fields to the <this._fields> ---++ Display the Fields Form
    finalizeDependentFields() {

        // Get the input elements from the radio group
        const radioInputs = this.querySelectorAll('[data-element="radio"]');

        if (radioInputs) {
            // Find the selected radio
            const selected = Array.from(radioInputs).find(radio => radio.checked).value;
            // Do nothing if no selection
            if (!selected) return;
            // Store the selected value
            this.dependentSelection = selected;
        } 

        // Add the dependent controlling's selected value to <this.fieldForm> 
        this.fieldForm[this._dependentFields.controlField.fieldName] = this.dependentSelection;

        // Get the dependent fields based on the <dependentSelection
        const newFields = this.dependentFields.dependentFields.filter(dpndt => dpndt.validValues.some(val => val === this.dependentSelection))
            .map(newField => newField.field); 
        
        // Update <this._requiredFields> && <this._fields>
        this.updateRequiredFieldsArr(newFields);
        this.updateFormFields(newFields);

        setTimeout(() => {
            const ele = this.querySelector('[data-element="fieldInput"]');
            if (ele) ele.focusMe();
        }, 30);

    }
    // Display a radio selection if there are dependent fields
    get isDependent() {
        return this._dependentFields && Object.keys(this._dependentFields).length > 0;
    }
    // Check if the last element is a piclist 
    get isLastPicklist() {
        const pls = ['picklist', 'multipicklist'];
        if ( this._fields && this._fields.length ) {
            return this._fields.length > 1 
                ? this._fields.slice(-2).some(f => pls.some(pl => pl === f?.dtype))
                : this._fields.slice(-1).some(f => pls.some(pl => pl === f?.dtype));
        }
        return false;
    }
    // ======================================================================= \\


    // =========================== Public Methods ============================ \\ 
    // Display the modal when parent commands
    @api showMe() {
        this.displayModal = true;
    }
    // Close the modal when parent commands
    @api hideMe() {
        this.displayModal = false;
    }
    @api focusMe() {
        if (this.dependentFields) {
            const radios = this.querySelectorAll('[data-element="radio"]');
            Array.from(radios)[0].focus();
            return;
        }
        const formFields = this.querySelectorAll('[data-element="fieldInput"]');
        if (formFields) {
            Array.from(formFields)[0].focus();
        }
    }
    // ======================================================================= \\


    // ========================= Events To Parent =========================== \\ 
    // Handle message sent from the child component --->  update the <fieldForm> object
    handleNewInput(event) {
        // Get the fieldName from the dataset-id
        const fieldName = event.currentTarget.dataset.id;

        // Get the message from the child
        const details = event.detail;
     
        // Flag required
        const isRequired = this.checkIfRequired(fieldName);

        // Get the value sent by the childs
        let val = details ? details[Object.keys(details)[0]] : null;
     
        // Flag if the user input was an error
        let isError = isRequired && !val ? true : false;
        let errorMessage = null;

        // Check if min/max is breached
        if (!isError) { 
            errorMessage = this.isMinMaxBreached(fieldName, val);
            isError = errorMessage?.length ? true : false;
        }

        // Update the fieldForm data to reflect the current value of the child
        this.fieldForm[fieldName] = val;
        console.log('here is this.fieldForms new valuiie before evaluating the formula: ', val);
        // Update formula fields
        this.updateFormulaFields();


        if (isError) {
            this.triggerError(fieldName, errorMessage);
        } else {
            this.unTriggerError(fieldName);
        }
    }
    // If data verified in handleSave() --> Save the data and close the modal
    async save() {
        this.displayModal = false;
        this.dispatchEvent(new CustomEvent('formmodalclick', { detail: this.fieldForm } ));
    }
    // ======================================================================= \\


    // ======================= Handle Required Fields ======================== \\ 
    // Return True if field is required
    checkIfRequired(fieldName) {
        return new Set(this._requiredFields).has(fieldName);
    }
    // Trigger Error to the child component if required field missing
    triggerError(fieldName, errMsg=null) {
        this.querySelector(`[data-id=${fieldName}]`).errorMe(errMsg);
    }
    unTriggerError(fieldName) {
        this.querySelector(`[data-id=${fieldName}]`).removeErrorMe();
    }
    // ======================================================================= \\


    // ===================== Handle Save/Cancel Buttons ====================== \\ 
    // Close the modal
    handleCancel() {
        this.displayModal = false;
        this.dispatchEvent(new CustomEvent('formmodalclick', {
            detail: 'cancel',
            bubbles: false,    
            composed: true     
        }));
    }
    // Verify that all the required fields have been filled
    async handleSave() {
        let isSave = true;

        // update formula fields
        this.updateFormulaFields();

        // Validate that all required fields are filled out
        for (const required of this._requiredFields) {
            if (!this.fieldForm[required]) {
                // Trigger an error message on the element  
                this.querySelector(`[data-id="${required}"]`).errorMe();
                isSave = false;
            }
        }

        for (const field of this._fields) {
            if (field?.fieldName) {
                const ele = this.querySelector(`[data-id="${field.fieldName}"]`);
                if (ele) {
                    const newVal = ele.getValue();
                    const minMaxMsg = this.isMinMaxBreached(field.fieldName, newVal);
                    if (minMaxMsg) {
                        this.querySelector(`[data-id="${field.fieldName}"]`).errorMe(minMaxMsg);
                        isSave = false;
                    }
                    this.fieldForm[field.fieldName] = newVal;
                }
            }
        }
        
        if (isSave) { 
            await this.save(); 
        }
    }
    // ======================================================================= \\

}